/**
 * buildingModels.tsx
 *
 * Procedural 3D building models for the campus overview.
 * Each building has a distinct silhouette and material treatment
 * that reflects its function in the game.
 *
 * Buildings are centered at origin. The parent Building3D component
 * handles positioning, rotation, and interaction.
 */

import React from 'react';
import * as THREE from 'three';
import { CampusLocation } from '../../../types/spatial';

interface BuildingModelProps {
  type: CampusLocation;
  color: string;
}

const GLASS = {
  color: '#a8c8d8',
  metalness: 0.85,
  roughness: 0.05,
  transparent: true as const,
  opacity: 0.55,
};

function darken(hex: string, amount: number): string {
  const c = new THREE.Color(hex);
  c.multiplyScalar(1 - amount);
  return `#${c.getHexString()}`;
}

function lighten(hex: string, amount: number): string {
  const c = new THREE.Color(hex);
  c.lerp(new THREE.Color('#ffffff'), amount);
  return `#${c.getHexString()}`;
}

/** Window band helper -- thin plane with glass material on a building face */
function WindowBand({
  position,
  rotation,
  args,
  opacity,
}: {
  position: [number, number, number];
  rotation?: [number, number, number];
  args: [number, number];
  opacity?: number;
}) {
  return (
    <mesh position={position} rotation={rotation}>
      <planeGeometry args={args} />
      <meshStandardMaterial {...GLASS} opacity={opacity ?? GLASS.opacity} />
    </mesh>
  );
}

/** Headquarters: Stepped modern tower with entrance canopy */
function Headquarters({ color }: { color: string }) {
  const base = darken(color, 0.3);
  const top = lighten(color, 0.15);
  return (
    <group>
      <mesh position={[0, -4.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[10, 3, 10]} />
        <meshStandardMaterial color={base} metalness={0.2} roughness={0.7} />
      </mesh>
      <mesh position={[0, -3, 5.5]} castShadow>
        <boxGeometry args={[5, 0.25, 2.5]} />
        <meshStandardMaterial color={base} metalness={0.4} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[8, 7, 8]} />
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.5} />
      </mesh>
      <mesh position={[0, 5, 0]} castShadow receiveShadow>
        <boxGeometry args={[6, 2, 6]} />
        <meshStandardMaterial color={top} metalness={0.35} roughness={0.4} />
      </mesh>
      <WindowBand position={[0, 0.5, 4.01]} args={[7, 5.5]} />
      <WindowBand position={[0, 0.5, -4.01]} args={[7, 5.5]} rotation={[0, Math.PI, 0]} />
      <WindowBand position={[-4.01, 0.5, 0]} args={[7, 5.5]} rotation={[0, -Math.PI / 2, 0]} />
      <WindowBand position={[4.01, 0.5, 0]} args={[7, 5.5]} rotation={[0, Math.PI / 2, 0]} />
      <mesh position={[1.5, 6.3, 1]} castShadow>
        <boxGeometry args={[1.5, 0.6, 1.5]} />
        <meshStandardMaterial color={base} metalness={0.5} roughness={0.6} />
      </mesh>
    </group>
  );
}

/** Design Studio: Angular form with cantilevered upper section */
function DesignStudio({ color }: { color: string }) {
  const base = darken(color, 0.3);
  const accent = lighten(color, 0.1);
  return (
    <group>
      <mesh position={[0, -3, 0]} castShadow receiveShadow>
        <boxGeometry args={[7, 2, 6]} />
        <meshStandardMaterial color={base} metalness={0.2} roughness={0.7} />
      </mesh>
      <mesh position={[-0.5, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[5, 4, 5]} />
        <meshStandardMaterial color={color} metalness={0.35} roughness={0.45} />
      </mesh>
      <mesh position={[1, 2.5, 0.5]} castShadow receiveShadow>
        <boxGeometry args={[5, 3, 4]} />
        <meshStandardMaterial color={accent} metalness={0.4} roughness={0.35} />
      </mesh>
      <WindowBand position={[1, 2.5, 2.51]} args={[4.5, 2.5]} />
      <WindowBand position={[3.51, 2.5, 0.5]} args={[3.5, 2.5]} rotation={[0, Math.PI / 2, 0]} />
      <WindowBand position={[-0.5, 0, 2.51]} args={[4, 3]} />
    </group>
  );
}

/** R&D Lab: Clean body with dome observatory and antenna */
function RDLab({ color }: { color: string }) {
  const base = darken(color, 0.3);
  return (
    <group>
      <mesh position={[0, -2.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[8, 1, 7]} />
        <meshStandardMaterial color={base} metalness={0.2} roughness={0.7} />
      </mesh>
      <mesh position={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[7, 4, 6]} />
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.5} />
      </mesh>
      <mesh position={[1, 2.5, 0]} castShadow>
        <sphereGeometry args={[2, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={lighten(color, 0.2)}
          metalness={0.5}
          roughness={0.2}
        />
      </mesh>
      <mesh position={[-2, 3.5, -1.5]} castShadow>
        <cylinderGeometry args={[0.08, 0.08, 3, 8]} />
        <meshStandardMaterial color="#666666" metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh position={[-2, 5, -1.5]}>
        <sphereGeometry args={[0.15, 8, 8]} />
        <meshStandardMaterial color="#ff4444" emissive="#ff4444" emissiveIntensity={0.8} />
      </mesh>
      <WindowBand position={[0, 0.5, 3.01]} args={[6, 2.5]} />
      <WindowBand position={[0, 0.5, -3.01]} args={[6, 2.5]} rotation={[0, Math.PI, 0]} />
    </group>
  );
}

/** Manufacturing: Wide industrial building with chimneys */
function Manufacturing({ color }: { color: string }) {
  const base = darken(color, 0.3);
  return (
    <group>
      <mesh position={[0, -2.1, 0]} castShadow receiveShadow>
        <boxGeometry args={[11, 0.8, 9]} />
        <meshStandardMaterial color={base} metalness={0.2} roughness={0.8} />
      </mesh>
      <mesh position={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[10, 3.5, 8]} />
        <meshStandardMaterial color={color} metalness={0.25} roughness={0.6} />
      </mesh>
      <mesh position={[5.5, -0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 2, 5]} />
        <meshStandardMaterial color={base} metalness={0.2} roughness={0.7} />
      </mesh>
      <mesh position={[-3, 3.5, -2.5]} castShadow>
        <cylinderGeometry args={[0.4, 0.5, 3.5, 8]} />
        <meshStandardMaterial color="#555555" metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh position={[-3, 5.3, -2.5]}>
        <cylinderGeometry args={[0.5, 0.4, 0.3, 8]} />
        <meshStandardMaterial color="#444444" metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh position={[-1, 3, 2]} castShadow>
        <cylinderGeometry args={[0.35, 0.45, 2.5, 8]} />
        <meshStandardMaterial color="#555555" metalness={0.5} roughness={0.5} />
      </mesh>
      <WindowBand position={[0, 0.8, 4.01]} args={[8, 1.2]} opacity={0.3} />
    </group>
  );
}

/** Sales Office: Professional building with prominent glass front */
function SalesOffice({ color }: { color: string }) {
  const base = darken(color, 0.3);
  return (
    <group>
      <mesh position={[0, -3, 0]} castShadow receiveShadow>
        <boxGeometry args={[6, 1, 6]} />
        <meshStandardMaterial color={base} metalness={0.2} roughness={0.7} />
      </mesh>
      <mesh position={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[5, 5, 5]} />
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.5} />
      </mesh>
      <mesh position={[0, -1.5, 3.5]} castShadow receiveShadow>
        <boxGeometry args={[3, 2, 2]} />
        <meshStandardMaterial color={base} metalness={0.25} roughness={0.6} />
      </mesh>
      <WindowBand position={[0, 0.5, 2.51]} args={[4.5, 4]} opacity={0.65} />
      <WindowBand
        position={[2.51, 0.5, 0]}
        args={[4, 3]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <mesh position={[0, 2.7, 0]} castShadow>
        <boxGeometry args={[5.2, 0.4, 5.2]} />
        <meshStandardMaterial color={base} metalness={0.3} roughness={0.6} />
      </mesh>
    </group>
  );
}

/** Executive Tower: Tapered skyscraper with multiple setbacks and spire */
function ExecutiveTower({ color }: { color: string }) {
  const base = darken(color, 0.3);
  const mid = lighten(color, 0.05);
  const top = lighten(color, 0.15);
  const crown = lighten(color, 0.25);
  return (
    <group>
      <mesh position={[0, -6.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[8, 2, 8]} />
        <meshStandardMaterial color={base} metalness={0.25} roughness={0.65} />
      </mesh>
      <mesh position={[0, -3.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[6, 4, 6]} />
        <meshStandardMaterial color={color} metalness={0.35} roughness={0.45} />
      </mesh>
      <mesh position={[0, 1, 0]} castShadow receiveShadow>
        <boxGeometry args={[5, 5, 5]} />
        <meshStandardMaterial color={mid} metalness={0.4} roughness={0.35} />
      </mesh>
      <mesh position={[0, 4.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[4, 2, 4]} />
        <meshStandardMaterial color={top} metalness={0.45} roughness={0.3} />
      </mesh>
      <mesh position={[0, 6, 0]} castShadow receiveShadow>
        <boxGeometry args={[3, 1, 3]} />
        <meshStandardMaterial color={crown} metalness={0.5} roughness={0.25} />
      </mesh>
      <mesh position={[0, 7.5, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.2, 2, 8]} />
        <meshStandardMaterial color="#888888" metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0, 8.6, 0]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#cccccc" emissive="#ff6666" emissiveIntensity={0.5} />
      </mesh>
      {/* Window bands on all four sides of mid section */}
      {([
        [0, 1, 2.51, 0],
        [0, 1, -2.51, Math.PI],
        [2.51, 1, 0, Math.PI / 2],
        [-2.51, 1, 0, -Math.PI / 2],
      ] as [number, number, number, number][]).map(([x, y, z, ry], i) => (
        <WindowBand key={i} position={[x, y, z]} args={[4, 4]} rotation={[0, ry, 0]} />
      ))}
    </group>
  );
}

/** Company Archives: Solid vault-like building with entrance columns */
function CompanyArchives({ color }: { color: string }) {
  const base = darken(color, 0.3);
  return (
    <group>
      <mesh position={[0, -1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[6, 1, 6]} />
        <meshStandardMaterial color={base} metalness={0.2} roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[5, 3, 5]} />
        <meshStandardMaterial color={color} metalness={0.25} roughness={0.6} />
      </mesh>
      {/* Entrance columns */}
      <mesh position={[-1.5, -0.5, 2.6]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 2, 8]} />
        <meshStandardMaterial color={base} metalness={0.3} roughness={0.5} />
      </mesh>
      <mesh position={[1.5, -0.5, 2.6]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 2, 8]} />
        <meshStandardMaterial color={base} metalness={0.3} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.6, 2.6]} castShadow>
        <boxGeometry args={[3.5, 0.4, 0.5]} />
        <meshStandardMaterial color={base} metalness={0.3} roughness={0.5} />
      </mesh>
      <WindowBand position={[0, 1.2, 2.51]} args={[1.5, 1]} opacity={0.4} />
      <mesh position={[0, 2.2, 0]} castShadow>
        <boxGeometry args={[5.4, 0.3, 5.4]} />
        <meshStandardMaterial color={base} metalness={0.3} roughness={0.7} />
      </mesh>
    </group>
  );
}

/** Main dispatch -- renders the appropriate building model based on type */
export const BuildingModel: React.FC<BuildingModelProps> = ({ type, color }) => {
  switch (type) {
    case CampusLocation.HEADQUARTERS:
      return <Headquarters color={color} />;
    case CampusLocation.DESIGN_STUDIO:
      return <DesignStudio color={color} />;
    case CampusLocation.RD_LAB:
      return <RDLab color={color} />;
    case CampusLocation.MANUFACTURING:
      return <Manufacturing color={color} />;
    case CampusLocation.SALES_OFFICE:
      return <SalesOffice color={color} />;
    case CampusLocation.EXECUTIVE_OFFICE:
      return <ExecutiveTower color={color} />;
    case CampusLocation.COMPANY_ARCHIVES:
      return <CompanyArchives color={color} />;
    default:
      return (
        <mesh castShadow receiveShadow>
          <boxGeometry args={[5, 5, 5]} />
          <meshStandardMaterial color={color} metalness={0.3} roughness={0.5} />
        </mesh>
      );
  }
};

export default BuildingModel;
